export interface PaletteItem {
  id: string;
  label: string;
  description?: string;
  keywords?: string[];
  icon: string;
  type: "navigation" | "action" | "entity" | "ai";
  category: string;
  path?: string;
  action?: () => void;
  severity?: "critical" | "warning" | "info";
  relevance?: number;
}

export interface QuickAction {
  icon: string;
  label: string;
  path: string;
}

export interface PaletteConfig {
  navigationCommands: PaletteItem[];
  quickActions: QuickAction[];
  actionShortcuts: PaletteItem[];
}

// ── Admin Navigation Commands (from sidebar.tsx) ──

const adminNavigationCommands: PaletteItem[] = [
  // Overview
  { id: "nav-dashboard", label: "Admin Dashboard", icon: "Home", type: "navigation", category: "Overview", path: "/" },
  { id: "nav-analytics", label: "Analytics & Reports", icon: "TrendingUp", type: "navigation", category: "Overview", path: "/admin/analytics" },
  { id: "nav-events-calendar", label: "Events Calendar", icon: "Calendar", type: "navigation", category: "Overview", path: "/events-calendar" },
  // Events
  { id: "nav-clients", label: "Clients", icon: "Users", type: "navigation", category: "Events", path: "/admin/clients", keywords: ["customer", "bride", "groom", "couple", "leads", "enquiry"] },
  { id: "nav-weddings", label: "Event Management", icon: "Heart", type: "navigation", category: "Events", path: "/admin/weddings", keywords: ["wedding", "event", "booking", "reservation"] },
  { id: "nav-event-day-forms", label: "Event Day Forms", icon: "Crown", type: "navigation", category: "Events", path: "/admin/stage-selections", keywords: ["form", "event form", "stage selection"] },
  { id: "nav-event-contacts", label: "Event Team Contacts", icon: "Users", type: "navigation", category: "Events", path: "/admin/event-team-contacts" },
  { id: "nav-reschedule", label: "Reschedule Requests", icon: "CalendarDays", type: "navigation", category: "Events", path: "/admin/reschedule-requests" },
  { id: "nav-reviews", label: "Review Management", icon: "Star", type: "navigation", category: "Events", path: "/admin/reviews" },
  { id: "nav-loyalty", label: "Loyalty Program", icon: "Gift", type: "navigation", category: "Events", path: "/admin/loyalty" },
  { id: "nav-live-photos", label: "Live Photos", icon: "Camera", type: "navigation", category: "Events", path: "/admin/live-photos" },
  { id: "nav-vision-studio", label: "Vision Studio", icon: "Eye", type: "navigation", category: "Events", path: "/visualizer" },
  // Finance
  { id: "nav-finance-hub", label: "Finance Hub", icon: "Wallet", type: "navigation", category: "Finance", path: "/admin/finance-hub", keywords: ["invoices", "pending invoices", "overdue invoices", "due invoices", "invoice", "payments", "revenue", "money", "billing"] },
  { id: "nav-payments", label: "Event Day Payments", icon: "Wallet", type: "navigation", category: "Finance", path: "/admin/payments", keywords: ["pay", "event payment", "payout"] },
  { id: "nav-maintenance", label: "Maintenance", icon: "Wrench", type: "navigation", category: "Finance", path: "/admin/internal-maintenance", keywords: ["repair", "fix", "broken"] },
  { id: "nav-local-purchase", label: "Local Purchase", icon: "ShoppingCart", type: "navigation", category: "Finance", path: "/admin/local-purchase", keywords: ["buy", "purchase order", "procurement"] },
  { id: "nav-catering-advances", label: "Catering Advances", icon: "CreditCard", type: "navigation", category: "Finance", path: "/admin/catering-advances", keywords: ["advance", "catering payment"] },
  { id: "nav-salary", label: "Salary Management", icon: "DollarSign", type: "navigation", category: "Finance", path: "/admin/salary", keywords: ["payroll", "salary", "wages", "staff payment"] },
  { id: "nav-invoices-admin", label: "Invoices", icon: "FileText", type: "navigation", category: "Finance", path: "/admin/finance-hub?tab=invoices", keywords: ["invoice", "tax invoice", "gst invoice", "bill", "billing", "issue invoice", "overdue", "balance", "receivable"] },
  { id: "nav-expenses", label: "Expenses", icon: "Wallet", type: "navigation", category: "Finance", path: "/admin/expenses", keywords: ["expense", "spending", "cost", "reimbursement"] },
  { id: "nav-accounting", label: "Accounting Dashboard", icon: "Calculator", type: "navigation", category: "Finance", path: "/admin/accounting", keywords: ["accounts", "ledger", "books"] },
  { id: "nav-gst-report", label: "GST Report", icon: "FileText", type: "navigation", category: "Finance", path: "/admin/gst-report" },
  { id: "nav-expense-report", label: "Expense Report", icon: "ClipboardList", type: "navigation", category: "Finance", path: "/admin/expense-report" },
  { id: "nav-zoho-settings", label: "Zoho Books Settings", icon: "Settings", type: "navigation", category: "Finance", path: "/zoho-settings" },
  // Operations
  { id: "nav-contacts", label: "Contacts", icon: "Contact", type: "navigation", category: "Operations", path: "/admin/operations-contacts" },
  { id: "nav-my-tasks", label: "My Tasks", icon: "ListTodo", type: "navigation", category: "Operations", path: "/admin/my-tasks" },
  { id: "nav-employees", label: "Employee Calendar", icon: "Users", type: "navigation", category: "Operations", path: "/admin/employees" },
  { id: "nav-mc-management", label: "MC Management", icon: "Crown", type: "navigation", category: "Operations", path: "/admin/mc-management" },
  { id: "nav-mc-schedules", label: "MC Schedules", icon: "Calendar", type: "navigation", category: "Operations", path: "/admin/mc-schedules" },
  { id: "nav-mc-calendar", label: "MC Calendar", icon: "CalendarDays", type: "navigation", category: "Operations", path: "/admin/mc-calendar" },
  { id: "nav-dance-calendar", label: "Dance Calendar", icon: "CalendarDays", type: "navigation", category: "Operations", path: "/admin/dance-calendar" },
  { id: "nav-valet", label: "Valet Management", icon: "Car", type: "navigation", category: "Operations", path: "/admin/valet-management" },
  { id: "nav-vr", label: "VR Experience", icon: "Gamepad2", type: "navigation", category: "Operations", path: "/admin/vr-management" },
  { id: "nav-domains", label: "Domain Requests", icon: "Globe", type: "navigation", category: "Operations", path: "/admin/domain-requests" },
  { id: "nav-inventory", label: "Inventory", icon: "Package", type: "navigation", category: "Operations", path: "/admin/inventory" },
  { id: "nav-venue-hub", label: "Venue Hub", icon: "Building2", type: "navigation", category: "Operations", path: "/admin/venue-management", keywords: ["vms", "venue management", "tasks", "cleaning", "facility"] },
  { id: "nav-amazon", label: "Amazon Business", icon: "Store", type: "navigation", category: "Operations", path: "/admin/amazon" },
  { id: "nav-payment-approval", label: "Payment Approval", icon: "CheckSquare", type: "navigation", category: "Operations", path: "/admin/salary-approval" },
  { id: "nav-salary-reports", label: "Salary Reports", icon: "BarChart3", type: "navigation", category: "Operations", path: "/admin/salary-reports" },
  { id: "nav-catering-mgmt", label: "Catering Management", icon: "Utensils", type: "navigation", category: "Operations", path: "/admin/catering" },
  { id: "nav-stage-designs", label: "Stage Design Management", icon: "Palette", type: "navigation", category: "Operations", path: "/admin/stage-designs" },
  // Vendors
  { id: "nav-vendor-mgmt", label: "Vendor Management", icon: "Store", type: "navigation", category: "Vendors", path: "/admin/vendors" },
  { id: "nav-vendor-approvals", label: "Vendor Approvals", icon: "ShoppingBag", type: "navigation", category: "Vendors", path: "/admin/vendor-management" },
  { id: "nav-addons", label: "Add-on Marketplace", icon: "ShoppingBag", type: "navigation", category: "Vendors", path: "/admin/addons" },
  { id: "nav-artists", label: "Artists Portfolio", icon: "Mic", type: "navigation", category: "Vendors", path: "/admin/artists" },
  // Communication
  { id: "nav-comm-hub", label: "Comm-Hub", icon: "MessageSquare", type: "navigation", category: "Communication", path: "/comm-hub" },
  { id: "nav-meetings", label: "Meetings", icon: "Video", type: "navigation", category: "Communication", path: "/meetings" },
  { id: "nav-whatsapp", label: "WhatsApp Support", icon: "MessageCircle", type: "navigation", category: "Communication", path: "/admin/live-chat-enterprise" },
  { id: "nav-support", label: "Support Tickets", icon: "Headphones", type: "navigation", category: "Communication", path: "/admin/support" },
  { id: "nav-announcements", label: "System Announcements", icon: "Megaphone", type: "navigation", category: "Communication", path: "/admin/announcements" },
  { id: "nav-wa-notifications", label: "WhatsApp Notifications", icon: "MessageSquare", type: "navigation", category: "Communication", path: "/admin/notifications" },
  { id: "nav-wa-templates", label: "WhatsApp Templates", icon: "MessageSquare", type: "navigation", category: "Communication", path: "/admin/whatsapp-templates" },
  { id: "nav-email", label: "Email Management", icon: "Mail", type: "navigation", category: "Communication", path: "/admin/email-management" },
  { id: "nav-zoho-desk", label: "Zoho Desk Settings", icon: "Headphones", type: "navigation", category: "Communication", path: "/zoho-desk-settings" },
  // AI
  { id: "nav-ai-dashboard", label: "AI Admin Dashboard", icon: "Bot", type: "navigation", category: "AI", path: "/admin/ai-dashboard" },
  { id: "nav-ai-knowledge", label: "AI Knowledge Base", icon: "Brain", type: "navigation", category: "AI", path: "/admin/ai-knowledge" },
  { id: "nav-ai-assistant", label: "LAMirage AI Management", icon: "MessageSquare", type: "navigation", category: "AI", path: "/admin/ai-assistant" },
  { id: "nav-agents", label: "Agents", icon: "Bot", type: "navigation", category: "AI", path: "/admin/agents", keywords: ["ai", "approve", "ops agent", "proposals"] },
  // Settings
  { id: "nav-users", label: "User Management", icon: "Users", type: "navigation", category: "Settings", path: "/admin/users" },
  { id: "nav-settings", label: "System Settings", icon: "Settings", type: "navigation", category: "Settings", path: "/admin/settings" },
  { id: "nav-mfa", label: "Multi-Factor Authentication", icon: "Shield", type: "navigation", category: "Settings", path: "/admin/mfa" },
  { id: "nav-venue-config", label: "Venue & Operations", icon: "Building2", type: "navigation", category: "Settings", path: "/admin/venue-config" },
  { id: "nav-welcome-config", label: "Welcome Messages", icon: "Heart", type: "navigation", category: "Settings", path: "/admin/welcome-config" },
  { id: "nav-mobile-nav", label: "Mobile Navigation", icon: "Smartphone", type: "navigation", category: "Settings", path: "/admin/mobile-navigation" },
  { id: "nav-mobile-app", label: "Mobile App Management", icon: "Smartphone", type: "navigation", category: "Settings", path: "/admin/mobile-app" },
  { id: "nav-otp-testing", label: "Global OTP Testing", icon: "Globe", type: "navigation", category: "Settings", path: "/admin/otp-testing" },
  // Data
  { id: "nav-storage", label: "Storage Management", icon: "HardDrive", type: "navigation", category: "Data", path: "/admin/storage" },
  { id: "nav-backups", label: "Backup Management", icon: "Database", type: "navigation", category: "Data", path: "/admin/backups" },
  { id: "nav-performance", label: "Performance Monitoring", icon: "Monitor", type: "navigation", category: "Data", path: "/admin/performance" },
  { id: "nav-crm", label: "CRM Activities", icon: "Activity", type: "navigation", category: "Data", path: "/admin/zoho-activities" },
];

// ── Event Manager Navigation Commands ──

const eventManagerNavigationCommands: PaletteItem[] = [
  { id: "nav-dashboard", label: "Dashboard", icon: "Home", type: "navigation", category: "Navigate", path: "/" },
  { id: "nav-clients", label: "Clients", icon: "Users", type: "navigation", category: "Navigate", path: "/admin/clients" },
  { id: "nav-event-day-forms", label: "Event Day Forms", icon: "Crown", type: "navigation", category: "Navigate", path: "/admin/stage-selections" },
  { id: "nav-events-calendar", label: "Events Calendar", icon: "Calendar", type: "navigation", category: "Navigate", path: "/events-calendar" },
  { id: "nav-mc-calendar", label: "MC Calendar", icon: "CalendarDays", type: "navigation", category: "Navigate", path: "/admin/mc-calendar" },
  { id: "nav-events-hub", label: "Events Hub", icon: "Heart", type: "navigation", category: "Navigate", path: "/admin/events-hub" },
  { id: "nav-finance-hub", label: "Finance Hub", icon: "Banknote", type: "navigation", category: "Navigate", path: "/admin/finance-hub" },
  { id: "nav-venue-hub", label: "Venue Hub", icon: "Building2", type: "navigation", category: "Navigate", path: "/admin/venue-management" },
  { id: "nav-valet", label: "Valet Management", icon: "Car", type: "navigation", category: "Navigate", path: "/admin/valet-management" },
  { id: "nav-my-tasks", label: "My Tasks", icon: "ListTodo", type: "navigation", category: "Navigate", path: "/admin/my-tasks" },
  { id: "nav-help", label: "Help", icon: "HelpCircle", type: "navigation", category: "Navigate", path: "/admin/help" },
];

// ── Operations Manager Navigation Commands ──

const operationsManagerNavigationCommands: PaletteItem[] = [
  { id: "nav-dashboard", label: "Dashboard", icon: "Home", type: "navigation", category: "Navigate", path: "/" },
  { id: "nav-clients", label: "Clients", icon: "Users", type: "navigation", category: "Navigate", path: "/admin/clients" },
  { id: "nav-events-calendar", label: "Events Calendar", icon: "Calendar", type: "navigation", category: "Navigate", path: "/events-calendar" },
  { id: "nav-finance-hub", label: "Finance Hub", icon: "Banknote", type: "navigation", category: "Navigate", path: "/admin/finance-hub" },
  { id: "nav-venue-hub", label: "Venue Hub", icon: "Building2", type: "navigation", category: "Navigate", path: "/admin/venue-management" },
  { id: "nav-salary-reports", label: "Salary Reports", icon: "BarChart3", type: "navigation", category: "Navigate", path: "/admin/salary-reports" },
  { id: "nav-support", label: "Support Tickets", icon: "Headphones", type: "navigation", category: "Navigate", path: "/admin/support" },
  { id: "nav-email", label: "Email Management", icon: "Mail", type: "navigation", category: "Navigate", path: "/admin/email-management" },
  { id: "nav-ai-knowledge", label: "AI Knowledge Base", icon: "Brain", type: "navigation", category: "Navigate", path: "/admin/ai-knowledge" },
  { id: "nav-my-tasks", label: "My Tasks", icon: "ListTodo", type: "navigation", category: "Navigate", path: "/admin/my-tasks" },
];

// ── Client Navigation Commands ──

function getClientNavigationCommands(weddingId?: number): PaletteItem[] {
  const items: PaletteItem[] = [
    { id: "nav-dashboard", label: "Dashboard", icon: "Home", type: "navigation", category: "Navigate", path: "/" },
    { id: "nav-bookings", label: "Bookings", icon: "Calendar", type: "navigation", category: "Navigate", path: "/bookings" },
    { id: "nav-invoices", label: "Invoices", icon: "CreditCard", type: "navigation", category: "Navigate", path: "/invoices" },
  ];

  if (weddingId) {
    items.push(
      { id: "nav-event-form", label: "Event Form", icon: "Palette", type: "navigation", category: "Navigate", path: "/event-form" },
      { id: "nav-story-weaver", label: "Story Weaver", icon: "Film", type: "navigation", category: "Navigate", path: "/story-weaver" },
      { id: "nav-mc-scheduling", label: "MC Scheduling", icon: "Mic", type: "navigation", category: "Navigate", path: "/mc-scheduling" },
      { id: "nav-vision-studio", label: "Vision Studio", icon: "Eye", type: "navigation", category: "Navigate", path: "/visualizer" },
    );
  }

  items.push(
    { id: "nav-addons", label: "Add-ons & Services", icon: "ShoppingBag", type: "navigation", category: "Navigate", path: "/client/addons" },
    { id: "nav-loyalty", label: "Loyalty Program", icon: "Gift", type: "navigation", category: "Navigate", path: "/loyalty-program" },
    { id: "nav-family-hub", label: "Family Hub", icon: "Users", type: "navigation", category: "Navigate", path: "/family-hub" },
    { id: "nav-website", label: "Wedding Website", icon: "Globe", type: "navigation", category: "Wedding Planning", path: "/website-v2" },
    { id: "nav-catering", label: "Catering Orders", icon: "Utensils", type: "navigation", category: "Wedding Planning", path: "/client/catering/order" },
    { id: "nav-budget", label: "Budget Calculator", icon: "Calculator", type: "navigation", category: "Wedding Planning", path: "/budget" },
    { id: "nav-invitations", label: "Wedding Invitations", icon: "Mail", type: "navigation", category: "Wedding Planning", path: "/invitations" },
    { id: "nav-vendors", label: "Vendor Directory", icon: "Store", type: "navigation", category: "Wedding Planning", path: "/vendors" },
    { id: "nav-my-vendors", label: "My Wedding Vendors", icon: "Heart", type: "navigation", category: "Wedding Planning", path: "/wedding-vendors" },
    { id: "nav-checklist", label: "Wedding Checklist", icon: "CheckSquare", type: "navigation", category: "Wedding Planning", path: "/checklist" },
    { id: "nav-timeline", label: "Timeline & Tasks", icon: "Calendar", type: "navigation", category: "Wedding Planning", path: "/ai-timeline" },
    { id: "nav-notes", label: "Handy Notes", icon: "StickyNote", type: "navigation", category: "Wedding Planning", path: "/notes" },
    { id: "nav-meetings", label: "Meetings", icon: "Video", type: "navigation", category: "Navigate", path: "/meetings" },
    { id: "nav-settings", label: "Settings", icon: "Settings", type: "navigation", category: "Navigate", path: "/settings" },
  );

  return items;
}

// ── Quick Actions Grids ──

const adminQuickActions: QuickAction[] = [
  { icon: "Users", label: "Clients", path: "/admin/clients" },
  { icon: "Wallet", label: "Finance Hub", path: "/admin/finance-hub" },
  { icon: "Calendar", label: "Events Calendar", path: "/events-calendar" },
  { icon: "MessageCircle", label: "Live Chat", path: "/admin/live-chat-enterprise" },
  { icon: "Building2", label: "VMS Hub", path: "/admin/venue-management" },
  { icon: "DollarSign", label: "Salary", path: "/admin/salary" },
  { icon: "BarChart3", label: "Analytics", path: "/admin/analytics" },
  { icon: "Wallet", label: "Expenses", path: "/admin/expenses" },
];

const eventManagerQuickActions: QuickAction[] = [
  { icon: "Calendar", label: "My Events", path: "/events-calendar" },
  { icon: "Users", label: "Clients", path: "/admin/clients" },
  { icon: "Crown", label: "MC Schedules", path: "/admin/mc-schedules" },
  { icon: "Palette", label: "Stage Selections", path: "/admin/stage-selections" },
  { icon: "ClipboardList", label: "Event Forms", path: "/admin/stage-selections" },
  { icon: "Utensils", label: "Catering", path: "/admin/catering" },
  { icon: "CalendarDays", label: "MC Calendar", path: "/admin/mc-calendar" },
  { icon: "Phone", label: "Event Contacts", path: "/admin/event-team-contacts" },
];

const operationsManagerQuickActions: QuickAction[] = [
  { icon: "Wrench", label: "VMS Tasks", path: "/admin/venue-management" },
  { icon: "ShoppingCart", label: "Local Purchase", path: "/admin/local-purchase" },
  { icon: "Wrench", label: "Maintenance", path: "/admin/internal-maintenance" },
  { icon: "Sparkles", label: "Cleaning", path: "/admin/venue-management" },
  { icon: "Contact", label: "Contacts", path: "/admin/operations-contacts" },
  { icon: "ListTodo", label: "Work Schedules", path: "/admin/operations-contacts" },
  { icon: "Package", label: "Inventory", path: "/admin/inventory" },
  { icon: "Truck", label: "Procurement", path: "/admin/venue-management" },
];

const clientQuickActions: QuickAction[] = [
  { icon: "Calendar", label: "My Bookings", path: "/bookings" },
  { icon: "CreditCard", label: "Invoices", path: "/invoices" },
  { icon: "ClipboardList", label: "Event Form", path: "/event-form" },
  { icon: "Globe", label: "Wedding Website", path: "/website-v2" },
  { icon: "Users", label: "Family Hub", path: "/family-hub" },
  { icon: "Utensils", label: "Catering", path: "/client/catering/order" },
  { icon: "Calculator", label: "Budget", path: "/budget" },
  { icon: "ShoppingBag", label: "Add-ons", path: "/client/addons" },
];

// ── Action Shortcuts ──

const adminActionShortcuts: PaletteItem[] = [
  { id: "act-new-booking", label: "Create new booking", icon: "Plus", type: "action", category: "Actions", path: "/bookings" },
  { id: "act-schedule-meeting", label: "Schedule a meeting", icon: "Video", type: "action", category: "Actions", path: "/meetings" },
  { id: "act-send-whatsapp", label: "Send WhatsApp message", icon: "MessageCircle", type: "action", category: "Actions", path: "/admin/live-chat-enterprise" },
  { id: "act-create-vms-task", label: "Create VMS task", icon: "Wrench", type: "action", category: "Actions", path: "/admin/venue-management" },
  { id: "act-add-employee", label: "Add new employee", icon: "UserPlus", type: "action", category: "Actions", path: "/admin/employees" },
  { id: "act-create-expense", label: "Create expense report", icon: "Receipt", type: "action", category: "Actions", path: "/admin/expenses" },
  { id: "act-gen-salary", label: "Generate salary session", icon: "DollarSign", type: "action", category: "Actions", path: "/admin/salary" },
];

const clientActionShortcuts: PaletteItem[] = [
  { id: "act-schedule-meeting", label: "Schedule a meeting", icon: "Video", type: "action", category: "Actions", path: "/meetings" },
  { id: "act-contact-support", label: "Contact support", icon: "Headphones", type: "action", category: "Actions", path: "/comm-hub" },
  { id: "act-view-invoice", label: "View my invoice", icon: "CreditCard", type: "action", category: "Actions", path: "/invoices" },
  { id: "act-manage-guests", label: "Manage guest list", icon: "Users", type: "action", category: "Actions", path: "/family-hub" },
  { id: "act-edit-website", label: "Edit wedding website", icon: "Globe", type: "action", category: "Actions", path: "/website-v2" },
];

// ── Search Function ──

export function searchCommands(query: string, commands: PaletteItem[]): PaletteItem[] {
  const q = query.toLowerCase().trim();
  if (!q) return [];

  const scored: { item: PaletteItem; score: number }[] = [];

  for (const item of commands) {
    const label = item.label.toLowerCase();
    const category = item.category.toLowerCase();
    const desc = (item.description || "").toLowerCase();
    const keywords = (item.keywords || []).map(k => k.toLowerCase());

    let score = 0;

    // Match against label
    if (label === q) {
      score = 100;
    } else if (label.startsWith(q)) {
      score = 80 + (q.length / label.length) * 10;
    } else if (q.startsWith(label)) {
      score = 70;
    } else if (label.includes(q)) {
      score = 50 + (q.length / label.length) * 10;
    }

    // Match against keywords (high priority)
    if (score === 0 && keywords.length > 0) {
      for (const kw of keywords) {
        if (kw === q) { score = Math.max(score, 95); break; }
        if (kw.startsWith(q)) { score = Math.max(score, 75); }
        else if (kw.includes(q)) { score = Math.max(score, 55); }
        else if (q.includes(kw)) { score = Math.max(score, 65); }
      }
    }

    // Match against category/description
    if (score === 0) {
      if (category.includes(q) || desc.includes(q)) {
        score = 30;
      }
    }

    // Multi-word matching
    if (score === 0) {
      const words = q.split(/\s+/);
      const allText = `${label} ${category} ${desc} ${keywords.join(' ')}`;
      const matchedWords = words.filter(w => allText.includes(w));
      if (matchedWords.length > 0) {
        score = 20 + (matchedWords.length / words.length) * 40;
      }
    }

    if (score > 0) {
      scored.push({ item: { ...item, relevance: score }, score });
    }
  }

  scored.sort((a, b) => b.score - a.score);
  return scored.slice(0, 8).map(s => s.item);
}

// ── Config Generators ──

export function getClientPaletteConfig(weddingId?: number): PaletteConfig {
  return {
    navigationCommands: getClientNavigationCommands(weddingId),
    quickActions: clientQuickActions,
    actionShortcuts: clientActionShortcuts,
  };
}

export function getAdminPaletteConfig(role: string): PaletteConfig {
  switch (role) {
    case "event_manager":
      return {
        navigationCommands: eventManagerNavigationCommands,
        quickActions: eventManagerQuickActions,
        actionShortcuts: adminActionShortcuts,
      };
    case "operations_manager":
      return {
        navigationCommands: operationsManagerNavigationCommands,
        quickActions: operationsManagerQuickActions,
        actionShortcuts: adminActionShortcuts,
      };
    default: // admin, accountant
      return {
        navigationCommands: adminNavigationCommands,
        quickActions: adminQuickActions,
        actionShortcuts: adminActionShortcuts,
      };
  }
}
